<template class="electric-vehicle-soft-search">
    <div class="electric-vehicle-soft-search__widget">
        <div class="electric-vehicle-soft-search__container">
            <form class="electric-vehicle-soft-search__form">
                <div class="electric-vehicle-soft-search__step" v-show="step === 1">
                    <h1 class="electric-vehicle-soft-search__title">Should I buy an electric car?</h1>
                    <h2>
                        Not sure if an electric car is right for you? Use our helpful guide by answering a few quick questions to discover
                        which car is best for you.
                    </h2>
                    <picture>
                        <source
                            type="image/webp"
                            srcset="
                                /images/articles/electric-vehicles/soft-search/welcome_step.webp,
                                /images/articles/electric-vehicles/soft-search/welcome_step@2x.webp 2x
                            "
                        />
                        <img
                            class="img-responsive electric-vehicle-soft-search__welcome-image"
                            srcset="
                                /images/articles/electric-vehicles/soft-search/welcome_step.jpg,
                                /images/articles/electric-vehicles/soft-search/welcome_step@2x.jpg 2x
                            "
                        />
                    </picture>
                    <button @click.prevent="step++" class="btn btn-primary">Start Quiz</button>
                </div>

                <div class="electric-vehicle-soft-search__step" v-show="step === 2">
                    <h2 class="electric-vehicle-soft-search__question">How many miles do you drive in a typical day?</h2>
                    <div class="electric-vehicle-soft-search__grid">
                        <label class="electric-vehicle-soft-search__step-button" for="miles_30">
                            <input
                                type="radio"
                                class="electric-vehicle-soft-search__radio-input"
                                v-model="averageDailyMiles"
                                value="30"
                                id="miles_30"
                            />
                            <img
                                class="img-responsive electric-vehicle-soft-search__step-image"
                                src="/images/articles/electric-vehicles/soft-search/step-2/less-than-30.svg"
                            />
                            <span class="electric-vehicle-soft-search__button-text">
                                <span class="electric-vehicle-soft-search__blue">Less</span>
                                than
                                <span class="electric-vehicle-soft-search__blue">50 miles</span>
                            </span>
                        </label>
                        <label class="electric-vehicle-soft-search__step-button" for="miles_50">
                            <input
                                type="radio"
                                class="electric-vehicle-soft-search__radio-input"
                                v-model="averageDailyMiles"
                                value="50"
                                id="miles_50"
                            />
                            <img
                                class="img-responsive electric-vehicle-soft-search__step-image"
                                src="/images/articles/electric-vehicles/soft-search/step-2/less-than-50.svg"
                            />
                            <span class="electric-vehicle-soft-search__button-text">
                                <span class="electric-vehicle-soft-search__orange">More</span>
                                than
                                <span class="electric-vehicle-soft-search__orange">50 miles</span>
                            </span>
                        </label>
                        <label class="electric-vehicle-soft-search__step-button" for="miles_100">
                            <input
                                type="radio"
                                class="electric-vehicle-soft-search__radio-input"
                                v-model="averageDailyMiles"
                                value="100"
                                id="miles_100"
                            />
                            <img
                                class="img-responsive electric-vehicle-soft-search__step-image"
                                src="/images/articles/electric-vehicles/soft-search/step-2/more-than-50.svg"
                            />
                            <span class="electric-vehicle-soft-search__button-text">
                                <span class="electric-vehicle-soft-search__green">More</span>
                                than
                                <span class="electric-vehicle-soft-search__green">100 miles</span>
                            </span>
                        </label>
                        <label class="electric-vehicle-soft-search__step-button" for="miles_150">
                            <input
                                type="radio"
                                class="electric-vehicle-soft-search__radio-input"
                                v-model="averageDailyMiles"
                                value="150"
                                id="miles_150"
                            />
                            <img
                                class="img-responsive electric-vehicle-soft-search__step-image"
                                src="/images/articles/electric-vehicles/soft-search/step-2/more-than-100.svg"
                            />
                            <span class="electric-vehicle-soft-search__button-text">
                                <span class="electric-vehicle-soft-search__orange">More</span>
                                than
                                <span class="electric-vehicle-soft-search__orange">150 miles</span>
                            </span>
                        </label>
                    </div>
                    <img
                        class="img-responsive electric-vehicle-soft-search__progress-bar"
                        src="/images/articles/electric-vehicles/soft-search/progress-bar-1.svg"
                    />
                </div>

                <div class="electric-vehicle-soft-search__step" v-show="step === 3">
                    <h2 class="electric-vehicle-soft-search__question">How often do you do this journey?</h2>
                    <button class="electric-vehicle-soft-search__prev-step" type="button" @click.prevent="prevStep">
                        &lt; Back a step
                    </button>
                    <div class="electric-vehicle-soft-search__grid">
                        <label class="electric-vehicle-soft-search__step-button" for="frequency_daily">
                            <input
                                type="radio"
                                class="electric-vehicle-soft-search__radio-input"
                                v-model="journeyFrequency"
                                value="daily"
                                id="frequency_daily"
                            />
                            <img
                                class="img-responsive electric-vehicle-soft-search__step-image"
                                src="/images/articles/electric-vehicles/soft-search/step-3/daily.svg"
                            />
                            <span class="electric-vehicle-soft-search__button-text">
                                <span class="electric-vehicle-soft-search__blue">Daily</span>
                            </span>
                        </label>
                        <label class="electric-vehicle-soft-search__step-button" for="frequency_weekdays">
                            <input
                                type="radio"
                                class="electric-vehicle-soft-search__radio-input"
                                v-model="journeyFrequency"
                                value="weekdays"
                                id="frequency_weekdays"
                            />
                            <img
                                class="img-responsive electric-vehicle-soft-search__step-image"
                                src="/images/articles/electric-vehicles/soft-search/step-3/weekday.svg"
                            />
                            <span class="electric-vehicle-soft-search__button-text">
                                <span class="electric-vehicle-soft-search__green">Monday</span>
                                to
                                <span class="electric-vehicle-soft-search__green">Friday</span>
                            </span>
                        </label>
                        <label class="electric-vehicle-soft-search__step-button" for="frequency_weekly">
                            <input
                                type="radio"
                                class="electric-vehicle-soft-search__radio-input"
                                v-model="journeyFrequency"
                                value="weekly"
                                id="frequency_weekly"
                            />
                            <img
                                class="img-responsive electric-vehicle-soft-search__step-image"
                                src="/images/articles/electric-vehicles/soft-search/step-3/weekly.svg"
                            />
                            <span class="electric-vehicle-soft-search__button-text">
                                <span class="electric-vehicle-soft-search__orange">Weekly</span>
                            </span>
                        </label>
                        <label class="electric-vehicle-soft-search__step-button" for="frequency_monthly">
                            <input
                                type="radio"
                                class="electric-vehicle-soft-search__radio-input"
                                v-model="journeyFrequency"
                                value="monthly"
                                id="frequency_monthly"
                            />
                            <img
                                class="img-responsive electric-vehicle-soft-search__step-image"
                                src="/images/articles/electric-vehicles/soft-search/step-3/monthly.svg"
                            />
                            <span class="electric-vehicle-soft-search__button-text">
                                <span class="electric-vehicle-soft-search__blue">Monthly</span>
                            </span>
                        </label>
                    </div>
                    <button
                        class="electric-vehicle-soft-search__prev-step electric-vehicle-soft-search__prev-step--mobile"
                        type="button"
                        @click.prevent="prevStep"
                    >
                        &lt; Back a step
                    </button>
                    <img
                        class="img-responsive electric-vehicle-soft-search__progress-bar"
                        src="/images/articles/electric-vehicles/soft-search/progress-bar-2.svg"
                    />
                </div>

                <div class="electric-vehicle-soft-search__step" v-show="step === 4">
                    <h2 class="electric-vehicle-soft-search__question">I mainly drive…</h2>
                    <button class="electric-vehicle-soft-search__prev-step" type="button" @click.prevent="prevStep">
                        &lt; Back a step
                    </button>
                    <div class="electric-vehicle-soft-search__grid">
                        <label class="electric-vehicle-soft-search__step-button" for="location_towns">
                            <input
                                type="radio"
                                class="electric-vehicle-soft-search__radio-input"
                                v-model="journeyLocation"
                                value="towns"
                                id="location_towns"
                            />
                            <img
                                class="img-responsive electric-vehicle-soft-search__step-image"
                                src="/images/articles/electric-vehicles/soft-search/step-4/towns.svg"
                            />
                            <span class="electric-vehicle-soft-search__button-text">
                                &hellip;in
                                <span class="electric-vehicle-soft-search__orange">towns</span>
                                or
                                <span class="electric-vehicle-soft-search__orange">cities</span>
                            </span>
                        </label>
                        <label class="electric-vehicle-soft-search__step-button" for="location_countryside">
                            <input
                                type="radio"
                                class="electric-vehicle-soft-search__radio-input"
                                v-model="journeyLocation"
                                value="countryside"
                                id="location_countryside"
                            />
                            <img
                                class="img-responsive electric-vehicle-soft-search__step-image"
                                src="/images/articles/electric-vehicles/soft-search/step-4/countryside.svg"
                            />
                            <span class="electric-vehicle-soft-search__button-text">
                                &hellip;in the
                                <span class="electric-vehicle-soft-search__green">countryside</span>
                            </span>
                        </label>
                        <label class="electric-vehicle-soft-search__step-button" for="location_motorways">
                            <input
                                type="radio"
                                class="electric-vehicle-soft-search__radio-input"
                                v-model="journeyLocation"
                                value="motorways"
                                id="location_motorways"
                            />
                            <img
                                class="img-responsive electric-vehicle-soft-search__step-image"
                                src="/images/articles/electric-vehicles/soft-search/step-4/motorways.svg"
                            />
                            <span class="electric-vehicle-soft-search__button-text">
                                &hellip;on
                                <span class="electric-vehicle-soft-search__blue">motorways</span>
                                and
                                <span class="electric-vehicle-soft-search__blue">a-roads</span>
                            </span>
                        </label>
                        <label class="electric-vehicle-soft-search__step-button" for="location_combination">
                            <input
                                type="radio"
                                class="electric-vehicle-soft-search__radio-input"
                                v-model="journeyLocation"
                                value="combination"
                                id="location_combination"
                            />
                            <img
                                class="img-responsive electric-vehicle-soft-search__step-image"
                                src="/images/articles/electric-vehicles/soft-search/step-4/combination.svg"
                            />
                            <span class="electric-vehicle-soft-search__button-text">
                                &hellip;a
                                <span class="electric-vehicle-soft-search__orange">combination</span>
                                of routes
                            </span>
                        </label>
                    </div>
                    <button
                        class="electric-vehicle-soft-search__prev-step electric-vehicle-soft-search__prev-step--mobile"
                        type="button"
                        @click.prevent="prevStep"
                    >
                        &lt; Back a step
                    </button>
                    <img
                        class="img-responsive electric-vehicle-soft-search__progress-bar"
                        src="/images/articles/electric-vehicles/soft-search/progress-bar-3.svg"
                    />
                </div>

                <template v-if="step === 5">
                    <h2 class="electric-vehicle-soft-search__question" v-if="electricOrHybrid == 'electric'">
                        According to your answers, an electric car is suited to your lifestyle. You can check out our range below:
                    </h2>
                    <h2 class="electric-vehicle-soft-search__question" v-else>
                        After taking a look at your answers, an electric car may not be suited for your lifestyle however you may suit a
                        hybrid. You can check out our range of hybrid cars below
                    </h2>

                    <div class="electric-vehicle-soft-search__vehicles-carousel-container">
                        <div
                            v-for="(vehicles, brand) in vehiclesKeyedByBrand"
                            :key="brand"
                            class="electric-vehicle-soft-search__vehicles-flex-item"
                        >
                            <soft-search-carousel :vehicles="vehicles" :electricOrHybrid="electricOrHybrid"></soft-search-carousel>
                        </div>
                    </div>

                    <button @click.prevent="retakeQuiz()" class="electric-vehicle-soft-search__retake-quiz-button">
                        take the quiz again
                    </button>
                </template>
            </form>
        </div>
    </div>
</template>

<script>
    import axios from 'axios';
    import EventObject from "../modules/tracking/EventObject";
    // import { MatchHeight } from "js-match-height";
    import SoftSearchCarousel from "./SoftSearchCarousel";
    // import SoftSearchVehicle from "./SoftSearchVehicle";

    export default {
        props: {
            brand: { type: String, default: "" },
        },
        components: {
            SoftSearchCarousel,
            // SoftSearchVehicle,
        },
        data: function () {
            return {
                averageDailyMiles: null,
                journeyFrequency: null,
                journeyLocation: null,
                step: 1,
                vehicles: {},
            };
        },
        watch: {
            averageDailyMiles(newValue, oldValue) {
                if (newValue != "" && newValue !== null) {
                    new EventObject("Electric Car - Soft Search", "Next Step", `Clicked To Step 3`).dispatch();
                    this.step = 3;
                }
            },
            journeyFrequency(newValue, oldValue) {
                if (newValue != "" && newValue !== null) {
                    new EventObject("Electric Car - Soft Search", "Next Step", `Clicked To Step 4`).dispatch();
                    this.step = 4;
                }
            },
            journeyLocation(newValue, oldValue) {
                if (newValue != "" && newValue !== null) {
                    new EventObject("Electric Car - Soft Search", "Next Step", `Clicked To Step 5`).dispatch();
                    this.submit();
                }
            },
        },
        computed: {
            vehiclesKeyedByBrand() {
                const filtered = Object.values(this.vehicles).filter((vehicle) => vehicle !== null);
                if (this.brand == "randd") {
                    return filtered;
                }

                return filtered.map((vehicle) => [vehicle]);
            },
            electricScore() {
                let score = 0;
                if (this.averageDailyMiles == "30" || this.averageDailyMiles == "50" || this.averageDailyMiles == "100") {
                    score++;
                }
                if (this.journeyFrequency == "monthly") {
                    score++;
                }
                if (this.journeyLocation == "towns" || this.journeyLocation == "combination" || this.journeyLocation == "countryside") {
                    score++;
                }

                return score;
            },
            hybridScore() {
                let score = 0;
                if (this.averageDailyMiles == "150") {
                    score++;
                }
                if (this.journeyFrequency == "daily" || this.journeyFrequency == "weekly" || this.journeyFrequency == "weekdays") {
                    score++;
                }
                // if (this.journeyLocation == 'motorways') {
                //     score++;
                // }

                return score;
            },
            electricOrHybrid() {
                return this.electricScore >= this.hybridScore ? "electric" : "hybrid";
            },
        },
        methods: {
            submit() {
                axios({
                    method: "GET",
                    url: "/api/electric-vehicle-soft-search/" + this.brand + "/" + this.electricOrHybrid,
                }).then((response) => {
                    this.vehicles = response.data;
                    this.step += 1;
                });
            },

            prevStep() {
                this.step--;
                if (this.step == 4) {
                    this.journeyLocation = null;
                    return;
                }
                if (this.step == 3) {
                    this.journeyFrequency = null;
                    return;
                }
                if (this.step == 2) {
                    this.averageDailyMiles = null;
                    return;
                }
            },

            retakeQuiz() {
                this.step = 2;
                this.averageDailyMiles = null;
                this.journeyFrequency = null;
                this.journeyLocation = null;
                this.vehicles = {};
                location.href = "#"; //required to work around a bug in WebKit (Chrome / Safari)
                location.href = "#electricGuide";
            },
        },
    };
</script>

<style lang="scss">
    .electric-vehicle-soft-search {
        &__widget {
            text-align: center;
            margin: 40px auto;
            max-width: 1170px;
            box-shadow: 0 0 5px rgba(black, 0.5);
            padding: 30px 15px;
            width: 100%;
            display: grid;
            place-items: center;

            @media (min-width: 992px) {
                padding: 20px;
                min-height: 450px;
            }
        }

        &__soft-search-container {
            max-width: 872px;
        }

        &__form {
            margin-top: 24px;
        }

        &__step {
            position: relative;
        }

        &__title {
            display: inline-block;
            width: 260px;
            font-size: 24px;
            text-transform: uppercase;
            margin-bottom: 20px;

            @media (min-width: 992px) {
                width: 100%;
            }
        }

        &__welcome-image {
            width: 100%;
            margin-bottom: 20px;
        }

        &__question {
            width: 100%;
            padding-left: 30px;
            padding-right: 30px;
            display: inline-block;
            min-height: 48px;
            font-size: 20;
            font-weight: bold;
            margin: 0;

            @media (min-width: 768px) {
                min-height: auto;
            }
        }

        &__grid {
            display: grid;
            grid-template-columns: 1fr;
            grid-template-rows: repeat(4, 120px);
            justify-items: stretch;
            align-items: stretch;
            gap: 15px;
            margin-top: 30px;

            @media (min-width: 768px) {
                grid-template-columns: repeat(2, minmax(210px, 1fr));
                grid-template-rows: repeat(2, 1fr);
                row-gap: 15px;
            }
            @media (min-width: 992px) {
                grid-template-columns: repeat(4, 210px);
                grid-template-rows: 1fr;
            }
        }

        &__step-button {
            display: flex;
            flex-direction: row;
            flex-wrap: nowrap;
            justify-content: space-between;
            align-items: center;
            background-color: white;
            border: none;
            box-shadow: 0 0 5px rgba(black, 0.5);
            cursor: pointer;
            transition: transform 200ms ease-out, box-shadow 200ms ease-out;
            padding: 15px;

            @media (min-width: 992px) {
                padding: 45px 30px 30px 30px;
                flex-direction: column;
                align-items: center;
            }

            &:hover {
                transform: scale(1.02);
                box-shadow: 0 0 7px rgba(black, 0.5);
            }
        }

        &__step-image {
            margin-right: 20px;
            height: 100%;

            @media (min-width: 768px) {
                margin-right: 30px;
                height: auto;
            }

            @media (min-width: 992px) {
                margin-left: auto;
                margin-right: auto;
                margin-bottom: 30px;
            }
        }

        &__button-text {
            width: 100%;
            text-align: center;
            font-size: 20px;
            font-weight: bold;
        }

        &__radio-input {
            display: none;
        }

        &__blue {
            color: #00a3e0;
        }

        &__green {
            color: #00a987;
        }

        &__red {
            color: #e8473f;
        }

        &__orange {
            color: #ffa100;
        }

        &__progress-bar,
        .progress-bar-desktop {
            margin-top: 30px;
            width: 100%;

            @media (min-width: 992px) {
                margin-top: 50px;
            }
        }

        &__prev-step {
            display: none;

            @media (min-width: 992px) {
                width: 100%;
                color: #00a3e0;
                border: none;
                background-color: transparent;
                font-size: 14px;
                font-weight: bold;
                padding: 10px 0;
                text-align: center;

                display: block;
                position: absolute;
                padding: 0;
                left: 0;
                top: 60px;
                width: auto;
                top: 0;
            }

            &--mobile {
                display: inline-block;
                color: #00a3e0;
                border: none;
                background-color: transparent;
                font-size: 14px;
                font-weight: bold;
                padding: 15px 30px;
                text-align: center;
                margin-top: 20px;

                @media (min-width: 992px) {
                    display: none;
                }
            }
        }

        &__vehicles-carousel-container {
            display: flex;
            margin-top: 55px;
            justify-content: center;
            flex-direction: column;
            align-items: center;
            flex-wrap: wrap;

            @media (min-width: 768px) {
                margin-top: 30px;
                flex-direction: row;
                align-items: stretch;
            }
        }

        &__vehicles-flex-item {
            width: 235px;
            padding-bottom: 30px;
            @media (min-width: 768px) {
                padding-bottom: 60px;
            }
            @media (min-width: 992px) {
                padding-bottom: 30px;
            }
        }

        &__last-step-vehicle-carousel {
            height: 100%;
        }

        &__slick-padding {
            height: 100%;
            padding: 5px;
        }

        &__vehicle-slide {
            box-shadow: 0 0 5px rgba(black, 0.5);
            padding: 20px;
            display: flex;
            flex-direction: column;
            flex-grow: 1;
            height: 100%;
        }

        &__image-container {
            flex-grow: 1;
            display: grid;
            place-items: center;
        }

        &__vehicle-image {
            width: 100%;
            height: auto;
            margin-bottom: 15px;
            max-width: 250px;
            margin-left: auto;
            margin-right: auto;
        }

        &__vehicle-content {
            display: flex;
            flex-direction: column;
            justify-content: space-between;
        }

        &__spec {
            padding-left: 0;
            text-align: center;

            @media (min-width: 992px) {
                text-align: left;
            }
        }

        &__spec-point {
            margin-bottom: 15px;

            p {
                margin-bottom: 0;
            }
        }

        &__spec-heading {
            font-weight: bold;
            margin-bottom: 5px;
        }

        &__vehicle-button {
            width: 100%;
        }

        &__retake-quiz-button {
            background-color: white;
            border: 1px solid #00a987;
            height: 45px;
            text-transform: uppercase;
            padding: 0 20px;
            color: #00a987;
            font-weight: bold;
            margin-top: 20px;

            @media (min-width: 992px) {
                margin-top: 45px;
            }
        }
    }

    .slick-track {
        display: flex;
    }

    .slick-list,
    .slick-track {
        height: 100%;
    }

    .slick-slide {
        height: 100%;

        > div {
            height: 100%;
        }
    }
</style>


// WEBPACK FOOTER //
// resources/assets/js/soft-search/SoftSearch.vue